"use client";

import type { ReactNode } from "react";

export const focusRing =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-studio-ink";

export const pillBase =
  "inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-pill px-5 text-[15px] font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60";

export const primaryButton = `${pillBase} ${focusRing} bg-studio-accent text-studio-on-accent hover:opacity-90`;
export const secondaryButton = `${pillBase} ${focusRing} border border-line bg-surface text-ink hover:border-line-strong`;

export function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-[22px] items-center rounded-pill bg-studio-accent-soft px-2 text-[11px] font-semibold text-studio-ink">
      {children}
    </span>
  );
}

/** A numbered checkout step: a white card with a title. */
export function Step({
  number,
  title,
  aside,
  children,
}: {
  number?: number;
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4 rounded-card border border-line bg-surface p-4 md:p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-3 text-base font-semibold text-ink md:text-lg">
          {number !== undefined && (
            <span
              aria-hidden="true"
              className="flex size-7 shrink-0 items-center justify-center rounded-pill bg-ink text-[13px] font-semibold text-surface"
            >
              {number}
            </span>
          )}
          {title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/**
 * One choice in a radio group, as a card. A real radio input sits inside the
 * label, so keyboards, screen readers and form semantics all work.
 */
export function RadioCard({
  name,
  value,
  checked,
  onChange,
  title,
  chip,
  line,
  trailing,
  children,
  disabled = false,
}: {
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  title: ReactNode;
  chip?: ReactNode;
  line?: ReactNode;
  trailing?: ReactNode;
  /** Extra details shown under the card while it's selected. */
  children?: ReactNode;
  /** A full room: shown, not selectable. */
  disabled?: boolean;
}) {
  return (
    <label
      className={[
        "flex flex-col gap-3 rounded-[14px] border bg-surface px-4 py-3.5 transition-colors duration-150 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-studio-ink md:px-[18px]",
        disabled
          ? "cursor-not-allowed border-line opacity-60"
          : checked
            ? "cursor-pointer border-2 border-studio-accent bg-studio-accent-soft"
            : "cursor-pointer border-line hover:border-line-strong",
      ].join(" ")}
    >
      <span className="flex items-center gap-3.5">
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={() => onChange(value)}
          className="sr-only"
        />
        <span
          aria-hidden="true"
          className={`size-5 shrink-0 rounded-pill bg-surface ${
            checked ? "border-[6px] border-studio-accent" : "border-[1.5px] border-line-strong"
          }`}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex flex-wrap items-center gap-2 text-[15px] font-semibold text-ink">
            {title}
            {chip}
          </span>
          {line && <span className="text-[13px] text-ashe">{line}</span>}
        </span>
        {trailing && (
          <span className="shrink-0 text-[15px] font-semibold tabular-nums text-ink">{trailing}</span>
        )}
      </span>
      {checked && children && <span className="block pl-[34px] text-sm text-ink-soft">{children}</span>}
    </label>
  );
}
